import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Paper, Grid, CircularProgress, Button, LinearProgress } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../i18n/LanguageContext';
import { API_URL } from '../utils';
import { useNavigate } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ShareIcon from '@mui/icons-material/Share';

interface YearlyStats {
    year: number;
    total_workouts: number;
    total_active_days: number;
    total_volume_kg: number;
    top_exercises: {
        name: string;
        count: number;
    }[];
    monthly_activity: {
        month: number;
        count: number;
    }[];
}

export const Results2025: React.FC = () => {
    const { fetchWithAuth, idToken } = useAuth();
    const { t } = useLanguage();
    const [stats, setStats] = useState<YearlyStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!idToken) return;

        const fetchStats = async () => {
            try {
                setLoading(true);
                // Hardcoded to 2025 as per requirement
                const response = await fetchWithAuth(`${API_URL}/analytics/wrapped/2025`);

                if (!response.ok) {
                    throw new Error(`Failed to load stats: ${response.status}`);
                }

                const data = await response.json();
                setStats(data);
            } catch (err: any) {
                console.error('Error fetching wrapped stats:', err);
                setError(t('wrapped.error'));
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [fetchWithAuth, idToken, t]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !stats) {
        return (
            <Container maxWidth="sm">
                <Box mt={4} textAlign="center">
                    <Typography variant="h5" color="error" gutterBottom>
                        {error || t('common.noData')}
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate('/')}>
                        Back to Dashboard
                    </Button>
                </Box>
            </Container>
        );
    }

    const maxMonthlyWorkouts = Math.max(...stats.monthly_activity.map(m => m.count), 1);

    // Month keys for translation
    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box textAlign="center" mb={6}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {t('wrapped.title')}
                </Typography>
                <Typography variant="h5" color="text.secondary">
                    {t('wrapped.subtitle')}
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {/* Big Numbers */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <FitnessCenterIcon sx={{ fontSize: 60, color: 'primary.main', mx: 'auto', mb: 2 }} />
                        <Typography variant="h3" fontWeight="bold">
                            {stats.total_workouts}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {t('wrapped.totalWorkouts')}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 3, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <EmojiEventsIcon sx={{ fontSize: 60, color: 'warning.main', mx: 'auto', mb: 2 }} />
                        <Typography variant="h4" fontWeight="bold">
                            {(stats.total_volume_kg / 1000).toFixed(1)}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {t('wrapped.tonnesLifted')}
                        </Typography>
                    </Paper>
                </Grid>

                {/* Top Exercises */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                            {t('wrapped.topExercises')}
                        </Typography>
                        <Box mt={2}>
                            {stats.top_exercises.map((exercise, index) => (
                                <Box key={exercise.name} mb={2}>
                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                        <Typography variant="body1" fontWeight={index === 0 ? 'bold' : 'normal'}>
                                            {index + 1}. {exercise.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {exercise.count}Sets
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(exercise.count / (stats.top_exercises[0]?.count || 1)) * 100}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            ))}
                            {stats.top_exercises.length === 0 && (
                                <Typography color="text.secondary">{t('wrapped.noExercises')}</Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Monthly Activity Chart */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                            {t('wrapped.consistency')}
                        </Typography>
                        <Box display="flex" height={200} mt={4} gap={0.5}>
                            {stats.monthly_activity.map((month) => {
                                const monthLabel = t(`monthsShort.${monthKeys[month.month - 1]}`);
                                // Check if we should only show the first letter
                                // Using CSS for responsiveness is generally better but for text manipulation simpler here
                                return (
                                    <Box key={month.month} flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end" height="100%">
                                        <Box
                                            sx={{
                                                width: '80%',
                                                bgcolor: 'primary.main',
                                                height: `${Math.min(Math.pow(month.count / maxMonthlyWorkouts, 0.5) * 85, 85)}%`,
                                                minHeight: month.count > 0 ? 8 : 0,
                                                borderRadius: '4px 4px 0 0', // Slightly rounded top corners
                                                transition: 'height 0.5s ease'
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                mt: 1,
                                                fontSize: { xs: '0.6rem', md: '0.7rem' },
                                                fontWeight: 'medium'
                                            }}
                                        >
                                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{monthLabel}</Box>
                                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{monthLabel.charAt(0)}</Box>
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Paper>
                </Grid>

                {/* Closing */}
                <Grid size={{ xs: 12 }}>
                    <Box textAlign="center" mt={4} mb={8}>
                        <Typography variant="h4" gutterBottom>
                            {t('wrapped.greatWork')}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                            {t('wrapped.keepPushing')}
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<ShareIcon />}
                            onClick={() => {
                                const shareText = t('wrapped.shareMessage', {
                                    workouts: stats.total_workouts,
                                    tonnes: (stats.total_volume_kg / 1000).toFixed(1)
                                });
                                navigator.share ? navigator.share({
                                    title: t('wrapped.shareTitle') || 'My 2025 Gym Wrapped',
                                    text: shareText
                                }).catch(() => { }) : alert('Share feature not supported on this browser');
                            }}
                        >
                            {t('wrapped.share')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );
};
